import './components/header.css';
import logo from './assets/stamp-logo-line.webp';
import emptyProfile from './assets/empty_profile.png';
import { Link } from 'react-router-dom';
import { useRef, useEffect, useState } from 'react';
import { useAuth } from './hooks/useAuth';
import { useLanguage } from './LanguageContext';

// 한글 번역이 아직 없어 Eng/Kor 전환 버튼을 숨겨둔다. 번역이 붙으면 true로
// 되돌리면 데스크톱/모바일 두 자리에 다시 나타난다.
const SHOW_LANGUAGE_SWITCHER = false;

// Rendered twice -- once in the header bar (desktop) and once inside the nav
// menu next to Home (mobile). CSS decides which of the two is visible, so the
// shared open/close state is never observable in both places at once.
function LanguageSwitcher({
    className,
    isOpen,
    setIsOpen,
    language,
    setLanguage,
}) {
    return (
        <div
            className={className}
            onClick={() => setIsOpen(!isOpen)}
            style={{ position: 'relative', cursor: 'pointer' }}
        >
            <div>{language === 'en' ? 'Eng' : 'Kor'}</div>
            <div className="header-arrow"></div>
            {isOpen && (
                <div
                    className="lang-dropdown"
                    style={{
                        position: 'absolute',
                        top: '120%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'white',
                        padding: '10px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        minWidth: '80px',
                    }}
                >
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            setLanguage('en');
                            setIsOpen(false);
                        }}
                        style={{
                            cursor: 'pointer',
                            fontWeight: language === 'en' ? 'bold' : 'normal',
                            textAlign: 'center',
                            color: '#333',
                        }}
                    >
                        Eng
                    </div>
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            setLanguage('ko');
                            setIsOpen(false);
                        }}
                        style={{
                            cursor: 'pointer',
                            fontWeight: language === 'ko' ? 'bold' : 'normal',
                            textAlign: 'center',
                            color: '#333',
                        }}
                    >
                        Kor
                    </div>
                </div>
            )}
        </div>
    );
}

export default function Header() {
    const { user, logout } = useAuth();
    const { language, setLanguage } = useLanguage();
    const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);

    const chopsticksRef = useRef(null);
    const navRef = useRef(null);
    const overlayRef = useRef(null);
    const headerRef = useRef(null);
    const lastScrollYRef = useRef(0);
    const tickingRef = useRef(false);

    function toggleMenu() {
        if (chopsticksRef.current) {
            chopsticksRef.current.classList.toggle('active');
        }
        if (navRef.current) {
            navRef.current.classList.toggle('active');
        }
        if (overlayRef.current) {
            overlayRef.current.classList.toggle('active');
        }

        document.body.style.overflow = navRef.current.classList.contains(
            'active',
        )
            ? 'hidden'
            : '';
    }

    // Close menu when clicking any link in the header. Header sits outside
    // <Routes> and never remounts, so a link that skips this leaves the menu --
    // and the body scroll lock -- stuck open on the page it navigated to. The
    // logo and profile links live in the header bar, outside <nav>, so the
    // listener is delegated from <header>: that also covers header-right, which
    // only renders its links once the user query resolves. Only the
    // .dropdown-trigger buttons open a submenu, so no link click conflicts.
    useEffect(() => {
        const header = headerRef.current;
        const nav = navRef.current;
        if (!header || !nav) return;

        const handleLinkClick = (e) => {
            if (!e.target.closest('a')) return;
            if (nav.classList.contains('active')) {
                toggleMenu();
            }
        };

        header.addEventListener('click', handleLinkClick);

        return () => {
            header.removeEventListener('click', handleLinkClick);
        };
    }, []);

    // Dropdown Menu functionality
    useEffect(() => {
        const nav = navRef.current;
        if (!nav) return;

        const dropdowns = nav.querySelectorAll('.dropdown');
        const handlers = [];

        // Dropdowns start closed by default (no active class)
        dropdowns.forEach((dropdown) => {
            const trigger = dropdown.querySelector('.dropdown-trigger');
            if (!trigger) return;

            const handleTriggerClick = (e) => {
                e.stopPropagation();
                e.preventDefault();

                // Toggle current dropdown
                dropdown.classList.toggle('active');
            };

            handlers.push({ trigger, handler: handleTriggerClick });
            trigger.addEventListener('click', handleTriggerClick);
        });

        // Cleanup
        return () => {
            handlers.forEach(({ trigger, handler }) => {
                trigger.removeEventListener('click', handler);
            });
        };
    }, []);

    // Handle header visibility based on scroll direction
    useEffect(() => {
        const header = headerRef.current;
        if (!header) return;

        const updateHeader = () => {
            const currentScrollY = window.scrollY;

            // Check scroll direction and position
            if (currentScrollY <= 0) {
                // At the top of the page
                header.classList.remove('header--hidden');
            } else if (currentScrollY > lastScrollYRef.current) {
                // Scrolling down
                header.classList.add('header--hidden');
            } else {
                // Scrolling up
                header.classList.remove('header--hidden');
            }

            lastScrollYRef.current = currentScrollY;
            tickingRef.current = false;
        };

        // Optimize scroll event
        const handleScroll = () => {
            if (!tickingRef.current) {
                window.requestAnimationFrame(() => {
                    updateHeader();
                });
                tickingRef.current = true;
            }
        };

        window.addEventListener('scroll', handleScroll);

        // Cleanup
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <header ref={headerRef} className={user ? 'header--authed' : undefined}>
            <div className="header-content">
                <div className="header-left">
                    <div
                        className="chopsticks"
                        onClick={toggleMenu}
                        ref={chopsticksRef}
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    {SHOW_LANGUAGE_SWITCHER && (
                        <LanguageSwitcher
                            className="translation translation--desktop"
                            isOpen={isLangDropdownOpen}
                            setIsOpen={setIsLangDropdownOpen}
                            language={language}
                            setLanguage={setLanguage}
                        />
                    )}
                </div>
                <div className="header-mid logo">
                    <Link to="/">
                        <img
                            src={logo}
                            className="logo-img"
                            alt="CHOPSTICKSTORY logo"
                        />
                        <p>CHOPSTICKSTORY</p>
                    </Link>
                </div>
                <div className="header-right">
                    {user ? (
                        <>
                            {/* 넓은 화면은 사진 + 이름, 좁은 화면은 사진만
                                보인다. 이름이 display: none이 되면 링크에
                                접근 가능한 이름이 남지 않으므로 aria-label로
                                항상 보강한다. */}
                            <Link
                                to="/mypage"
                                className="header-user-name"
                                aria-label={user.name || user.username}
                            >
                                <img
                                    className="header-avatar"
                                    src={user.profile_photo || emptyProfile}
                                    alt=""
                                />
                                <span className="header-user-label">
                                    {user.name || user.username}
                                </span>
                            </Link>
                            <button
                                type="button"
                                className="header-logout"
                                onClick={logout}
                            >
                                Logout
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login">Login</Link>
                            <Link to="/signup" className="header-signup">
                                Sign up
                            </Link>
                        </>
                    )}
                </div>
            </div>

            <nav aria-label="Main navigation" ref={navRef}>
                <div className="menu-links">
                    <div className="menu-home-row">
                        <Link to="/" aria-current="page">
                            Home
                        </Link>
                        {SHOW_LANGUAGE_SWITCHER && (
                            <LanguageSwitcher
                                className="translation translation--mobile"
                                isOpen={isLangDropdownOpen}
                                setIsOpen={setIsLangDropdownOpen}
                                language={language}
                                setLanguage={setLanguage}
                            />
                        )}
                    </div>

                    <div className="dropdown">
                        <div className="dropdown-header">
                            <Link to="/about" className="menu-link">
                                About Us
                            </Link>
                            <button
                                className="dropdown-trigger"
                                aria-label="Toggle About Us dropdown"
                            >
                                <i
                                    className="fa-solid fa-chevron-down"
                                    aria-hidden="true"
                                ></i>
                            </button>
                        </div>
                        <div
                            className="dropdown-menu"
                            aria-label="About Us submenu"
                        >
                            <Link to="/about#org-intro">
                                About CHOPSTICKSTORY
                            </Link>
                            {/* "Our Team" is hidden while the roster is still
                                placeholder data. Restore alongside
                                SHOW_TEAM_SECTION in pages/About.js. */}

                            {/* Relocate the project location */}
                        </div>
                    </div>

                    <div className="dropdown">
                        <div className="dropdown-header">
                            <Link to="/project">Project</Link>
                        </div>
                    </div>

                    <div className="dropdown">
                        <div className="dropdown-header">
                            <Link to="/programs" className="menu-link">
                                Programs
                            </Link>
                            <button
                                className="dropdown-trigger"
                                aria-label="Toggle Programs dropdown"
                            >
                                <i
                                    className="fa-solid fa-chevron-down"
                                    aria-hidden="true"
                                ></i>
                            </button>
                        </div>
                        <div
                            className="dropdown-menu"
                            aria-label="Programs submenu"
                        >
                            <Link to="/programs#chopsticks">Chopsticks</Link>
                            <Link to="/programs#lotus-lantern">
                                Lotus Lantern
                            </Link>
                            <Link to="/programs#dancheong">Dancheong</Link>
                            <Link to="/programs#k-archery">K-Archery</Link>
                            <Link to="/programs#hanji-lantern">
                                Hanji Lantern
                            </Link>
                            <Link to="/programs#mother-of-pearl">
                                Mother-of-Pearl
                            </Link>
                        </div>
                    </div>

                    <div className="dropdown">
                        <div className="dropdown-header">
                            <Link to="/event" className="menu-link">
                                Events
                            </Link>
                        </div>
                    </div>

                    {/* <div className="dropdown">
                        <div className="dropdown-header">
                            <Link to="/project" className="menu-link">
                                Projects
                            </Link>
                        </div>
                    </div> */}
                </div>
                <div className="social-links">
                    <a
                        href="https://www.instagram.com/chopstickstory/"
                        title="Instagram"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <i className="fa-brands fa-instagram fa-xl"></i>
                    </a>
                    <a
                        href="https://www.linkedin.com/company/chopstickstory/"
                        title="Linkedin"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <i className="fa-brands fa-linkedin-in fa-xl"></i>
                    </a>
                    <a
                        href="https://www.youtube.com/@CHOPSTICKSTORY"
                        title="Youtube"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <i className="fa-brands fa-youtube fa-xl"></i>
                    </a>
                </div>
                <div className="external-links">
                    {/* No target="_blank" -- the mail client opens regardless and
                        some browsers are left with a stranded blank tab. */}
                    <a href="mailto:info@chopstickstory.org">
                        <i className="fa-regular fa-envelope"></i>
                        Contact
                        <i className="fa-solid fa-arrow-up-from-bracket"></i>
                    </a>
                    <a
                        href="https://www.paypal.com/donate/?hosted_button_id=WZK3AX6WV9RMC"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <i className="fa-brands fa-paypal"></i>
                        Donation
                        <i className="fa-solid fa-arrow-up-from-bracket"></i>
                    </a>
                </div>
            </nav>

            <div
                className="overlay"
                ref={overlayRef}
                onClick={toggleMenu}
            ></div>
        </header>
    );
}
